import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getApiErrorMessage,
  getMixRecipientConfigs,
  getCustomerGroups,
  upsertMixRecipientConfig,
} from '../../../../api/mix-deliveriesApi';

const EMPTY_FORM = {
  recipientNumber: '',
  recipientName: '',
  customerGroupCode: '',
  sandwichingRequired: true,
  isActive: true,
};

export default function MixRecipientConfigsTable({
  refreshKey = 0,
  onDataChanged,
}) {
  const [rows, setRows] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const requestId = useRef(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecipientNumber, setEditingRecipientNumber] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dialogError, setDialogError] = useState(null);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
  });

  const loadConfigs = useCallback(async () => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await getMixRecipientConfigs({
        page: page + 1, limit, search: search || undefined,
      });
      if (currentRequest !== requestId.current) return;
      const lastPage = Math.max(0, Math.ceil(result.total / limit) - 1);
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setRows(result.items);
      setTotal(result.total);
    } catch (requestError) {
      if (currentRequest !== requestId.current) return;
      setRows([]);
      setError(getApiErrorMessage(requestError));
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    loadConfigs();
    return () => { requestId.current += 1; };
  }, [loadConfigs, refreshKey]);

  useEffect(() => {
    let active = true;
    getCustomerGroups()
      .then((groups) => { if (active) setCustomerGroups(groups); })
      .catch((requestError) => {
        if (active) setError(getApiErrorMessage(requestError));
      });
    return () => { active = false; };
  }, []);

  const handleOpenCreate = () => {
    setEditingRecipientNumber(null);
    setForm(EMPTY_FORM);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (config) => {
    setEditingRecipientNumber(config.recipientNumber);

    setForm({
      recipientNumber: config.recipientNumber ?? '',
      recipientName: config.recipientName ?? '',
      customerGroupCode: config.customerGroupCode ?? '',
      sandwichingRequired: Boolean(config.sandwichingRequired),
      isActive: Boolean(config.isActive),
    });

    setDialogError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (saving) {
      return;
    }

    setDialogOpen(false);
    setEditingRecipientNumber(null);
    setForm(EMPTY_FORM);
    setDialogError(null);
  };

  const handleSave = async () => {
    const recipientNumber = form.recipientNumber.trim();

    if (!recipientNumber) {
      setDialogError('Numer odbiorcy jest wymagany.');
      return;
    }

    setSaving(true);
    setDialogError(null);

    try {
      const result = await upsertMixRecipientConfig(recipientNumber, {
        recipientName: form.recipientName,
        customerGroupCode: form.customerGroupCode || null,
        sandwichingRequired: form.sandwichingRequired,
        isActive: form.isActive,
      });

      await loadConfigs();

      setDialogOpen(false);
      setEditingRecipientNumber(null);
      setForm(EMPTY_FORM);

      setNotification({
        open: true,
        message: buildSuccessMessage(result),
      });

      if (Number(result?.affectedDeliveriesCount ?? 0) > 0) {
        onDataChanged?.();
      }
    } catch (requestError) {
      setDialogError(getApiErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const handleApplySearch = () => {
    setPage(0);
    setSearch(searchInput.trim());
  };

  const handleClearSearch = () => {
    setPage(0);
    setSearchInput('');
    setSearch('');
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={2}
          alignItems={{
            xs: 'stretch',
            md: 'center',
          }}
        >
          <TextField
            size="small"
            label="Szukaj odbiorcy"
            placeholder="Numer lub nazwa odbiorcy"
            value={searchInput}
            disabled={loading}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleApplySearch();
              }
            }}
            sx={{
              minWidth: {
                xs: '100%',
                md: 320,
              },
            }}
          />

          <Box
            sx={{
              display: 'flex',
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              disabled={loading}
              onClick={handleApplySearch}
            >
              Szukaj
            </Button>

            <Button
              variant="outlined"
              disabled={loading}
              onClick={handleClearSearch}
            >
              Wyczyść
            </Button>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{
              ml: {
                md: 'auto',
              },
            }}
          >
            Dodaj odbiorcę
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Numer odbiorcy</TableCell>
              <TableCell>Nazwa odbiorcy</TableCell>
              <TableCell>Grupa / min. pozostała przydatność</TableCell>
              <TableCell>Kanapkowanie</TableCell>
              <TableCell>Konfiguracja aktywna</TableCell>
              <TableCell>Ostatnia zmiana</TableCell>
              <TableCell align="right">Akcja</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              rows.map((config) => (
                <TableRow
                  key={config.id ?? config.recipientNumber}
                  hover
                  sx={{
                    opacity: config.isActive ? 1 : 0.65,
                  }}
                >
                  <TableCell>
                    <Typography fontWeight={700}>
                      {config.recipientNumber}
                    </Typography>
                  </TableCell>

                  <TableCell>{config.recipientName ?? '—'}</TableCell>

                  <TableCell>
                    {config.customerGroup
                      ? formatCustomerGroup(config.customerGroup)
                      : 'Nie przypisano'}
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={config.sandwichingRequired ? 'TAK' : 'NIE'}
                      color={config.sandwichingRequired ? 'warning' : 'default'}
                      variant={
                        config.sandwichingRequired ? 'filled' : 'outlined'
                      }
                      sx={{
                        fontWeight: config.sandwichingRequired ? 700 : 400,
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={config.isActive ? 'AKTYWNA' : 'NIEAKTYWNA'}
                      color={config.isActive ? 'success' : 'default'}
                      variant={config.isActive ? 'filled' : 'outlined'}
                    />
                  </TableCell>

                  <TableCell>{formatDateTime(config.updatedAt)}</TableCell>

                  <TableCell align="right">
                    <Tooltip title="Edytuj konfigurację">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(config)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  Brak konfiguracji spełniających kryteria.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={limit}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="Klientów na stronie:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
        getItemAriaLabel={(type) => type === 'next' ? 'Następna strona' : 'Poprzednia strona'}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => {
          setLimit(Number(event.target.value));
          setPage(0);
        }}
        SelectProps={{ disabled: loading }}
        backIconButtonProps={{ disabled: loading || page === 0 }}
        nextIconButtonProps={{ disabled: loading || (page + 1) * limit >= total }}
      />

      <RecipientConfigDialog
        customerGroups={customerGroups}
        open={dialogOpen}
        editing={Boolean(editingRecipientNumber)}
        form={form}
        saving={saving}
        error={dialogError}
        onChange={setForm}
        onClose={handleCloseDialog}
        onSave={handleSave}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() =>
          setNotification({
            open: false,
            message: '',
          })
        }
        message={notification.message}
      />
    </Stack>
  );
}

function RecipientConfigDialog({
  customerGroups,
  open,
  editing,
  form,
  saving,
  error,
  onChange,
  onClose,
  onSave,
}) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {editing
          ? 'Edytuj konfigurację odbiorcy'
          : 'Dodaj konfigurację odbiorcy'}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Numer odbiorcy"
            value={form.recipientNumber}
            disabled={saving || editing}
            required
            autoFocus={!editing}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                recipientNumber: event.target.value,
              }))
            }
            inputProps={{
              maxLength: 50,
            }}
          />

          <TextField
            label="Nazwa odbiorcy"
            value={form.recipientName}
            disabled={saving}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                recipientName: event.target.value,
              }))
            }
            inputProps={{
              maxLength: 255,
            }}
          />

          <TextField
            select
            label="Grupa klienta"
            value={form.customerGroupCode}
            disabled={saving}
            helperText="Minimalna pozostała przydatność: procent całkowitego okresu lub liczba dni."
            onChange={(event) => onChange((current) => ({
              ...current,
              customerGroupCode: event.target.value,
            }))}
          >
            <MenuItem value="">Nie przypisano</MenuItem>
            {customerGroups.map((group) => (
              <MenuItem key={group.code} value={group.code}>
                {formatCustomerGroup(group)}
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            control={
              <Switch
                checked={form.sandwichingRequired}
                disabled={saving}
                onChange={(event) =>
                  onChange((current) => ({
                    ...current,
                    sandwichingRequired: event.target.checked,
                  }))
                }
              />
            }
            label="Kanapkowanie wymagane"
          />

          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                disabled={saving}
                onChange={(event) =>
                  onChange((current) => ({
                    ...current,
                    isActive: event.target.checked,
                  }))
                }
              />
            }
            label="Konfiguracja aktywna"
          />

          {!form.isActive && (
            <Alert severity="info">
              Nieaktywna konfiguracja jest traktowana jak brak wymagania
              kanapkowania. Aktywne dostawy tego odbiorcy zostaną ustawione na
              „Kanapkowanie: NIE”.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Anuluj
        </Button>

        <Button variant="contained" onClick={onSave} disabled={saving}>
          {saving ? 'Zapisywanie...' : 'Zapisz'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function buildSuccessMessage(result) {
  const affected = Number(result?.affectedDeliveriesCount ?? 0);
  const reviewRequired = Number(result?.reviewRequiredCount ?? 0);
  const resetToPrepare = Number(result?.resetToPrepareCount ?? 0);

  const parts = ['Konfiguracja odbiorcy została zapisana.'];

  if (affected > 0) {
    parts.push(`Zmienione aktywne dostawy: ${affected}.`);
  }

  if (reviewRequired > 0) {
    parts.push(`Do sprawdzenia po przygotowaniu: ${reviewRequired}.`);
  }

  if (resetToPrepare > 0) {
    parts.push(`Cofnięte do przygotowania: ${resetToPrepare}.`);
  }

  return parts.join(' ');
}

function formatCustomerGroup(group) {
  const requirement = group.minRemainingShelfLifeDays != null
    ? `${group.minRemainingShelfLifeDays} dni`
    : `${new Intl.NumberFormat('pl-PL').format(Number(group.minRemainingShelfLifePercent))}%`;
  return `${group.name} — ${requirement}`;
}

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  return new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}
